import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldFormula, VariableOption } from "@/types/formula";
import { FormulaEditor } from "./formula-editor";
import { VariableSuggestions } from "./variable-suggestions";

type FormulaModalProps = {
    id: string;
    /** What the field is called, so the box says which field is being written. */
    label: string;
    /** The sentence the server wrote about the field, when it wrote one. */
    description?: string;
    formula: FieldFormula;
    /** The expression itself, mark already removed by the field. */
    value: string;
    /** The catalogue the field already fetched: opening this must not cost a round trip. */
    options: VariableOption[];
    loadingOptions?: boolean;
    onChange: (value: string) => void;
    onClose: () => void;
};

/**
 * The expression editor with room to work, opened from the field it edits.
 *
 * Not the panel's input in a bigger box. The panel is 400px wide and every ergonomic
 * decision there is a concession to that: eleven-pixel type, the vocabulary folded away,
 * the diagnostics cut to one line. A modal has none of those constraints, so what was
 * folded for want of space unfolds — the variables become a column that stays open beside
 * the source instead of a dropdown that appears and goes, the functions are listed rather
 * than offered, and the source box is ten lines because a formula worth opening a modal for
 * is a formula worth seeing whole.
 *
 * It writes through to the form on every keystroke, exactly like the field it was opened
 * from. There is no draft and no « apply »: the autosave downstream expects one value per
 * keystroke, and a modal holding a second copy of the value would be a second place for it
 * to be lost — the button below says « Done » because closing is all it does.
 */
export function FormulaModal({ id, label, description, formula, value, options, loadingOptions, onChange, onClose }: FormulaModalProps) {
    return (
        <Dialog open onOpenChange={open => (open ? undefined : onClose())}>
            <DialogContent className="max-w-4xl" data-testid={`formula-modal-${id}`}>
                <DialogHeader>
                    <DialogTitle>{label}</DialogTitle>
                    <DialogDescription>
                        {description ? `${description} ` : ""}
                        {formula.mode === "template"
                            ? "This field is text with variables: write {{ to insert one, and it is filled for each card when the step runs."
                            : `This field is an expression: it is computed for each card, and what it must yield is ${formula.expects === "any" ? "any value" : formula.expects}.`}
                    </DialogDescription>
                </DialogHeader>

                <FormulaEditor
                    id={`${id}-modal`}
                    value={value}
                    formula={formula}
                    options={options}
                    loadingOptions={loadingOptions}
                    rows={10}
                    roomy
                    onChange={onChange}
                    aside={insert => <VariablePanel options={options} loading={loadingOptions} onPick={insert} />}
                />

                <DialogFooter>
                    <Button type="button" onClick={onClose}>
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * The variables, as a column that stays.
 *
 * The same list the field offers under the caret, searched from a box of its own rather
 * than from what is being typed: an author who opens this is looking for a name they do not
 * know, and the dropdown only ever answers about the word they have already started.
 */
function VariablePanel({ options, loading, onPick }: { options: VariableOption[]; loading?: boolean; onPick: (path: string) => void }) {
    const [query, setQuery] = useState("");

    return (
        <aside className="flex w-72 shrink-0 flex-col gap-1" data-testid="formula-modal-variables">
            <input
                value={query}
                spellCheck={false}
                placeholder="Search the variables"
                aria-label="Search the variables"
                onChange={event => setQuery(event.target.value)}
                className="h-8 w-full rounded border border-input bg-transparent px-2 text-xs"
            />
            <VariableSuggestions
                options={options}
                query={query}
                loading={loading}
                /* A way in walks the panel one segment deeper; a value is written into the
                   source. Same reading as the dropdown's, which is what keeps a namespace
                   from being inserted as if it were a field. */
                onPick={(path, continues) => (continues ? setQuery(`${path}.`) : onPick(path))}
                className="max-h-80 shadow-none"
            />
        </aside>
    );
}
